import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  Collapse,
  CustomInput,
  FormGroup,
  Label,
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useQuery } from 'js/hooks/useQuery';
import { useCapabilityMetrics } from 'js/hooks/useCapabilityMetrics';
import { useStandardCollator } from 'js/utils/string';
import { SCHEMAS } from 'js/api/schemas';
import { listCapabilitySetAssessments } from 'js/actions/capability-actions';
import {
  buildCapabilityElementChartOptions,
  buildCapabilityElementScores,
  capabilitiesWithAnyScores,
  flattenCapabilityTree,
  pickDefaultElementKey,
  pruneCapabilityTree,
  sameIdSet,
  selectedCapabilitiesInTreeOrder,
} from 'js/utils/capability-treemap-scores';
import CapabilityTreemap from 'js/components/Charts/CapabilityTreemap';
import UserGroupFilterCombobox, {
  getUserGroupFilterLabel,
} from './UserGroupFilterCombobox';
import CapabilityFilterCombobox, {
  getCapabilityFilterLabel,
} from './CapabilityFilterCombobox';
import { CapabilityOverviewChartGrid } from './OverviewCharts';
import styles from './CapabilitySetResults.module.scss';
import { Icon, Loading } from 'js/components';
import { faSpiderWeb } from '@fortawesome/pro-light-svg-icons';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';

function CapabilitySetResults({ capabilitySet }) {
  const { capabilitySetId } = useParams();
  const dispatch = useDispatch();
  const collator = useStandardCollator();

  const queryAssessments = useCallback(
    (queryId) =>
      dispatch(listCapabilitySetAssessments(capabilitySetId, queryId)),
    [dispatch, capabilitySetId]
  );

  const {
    refresh,
    result: assessments,
    hasLoaded,
    isLoading,
  } = useQuery({
    queryId: `capability-set-assessments-${capabilitySetId}`,
    query: queryAssessments,
    schema: SCHEMAS.USER_GROUP_ASSESSMENT_ARRAY,
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const userGroups = useMemo(() => {
    const byId = {};
    (assessments || []).forEach((uga) => {
      const ug = uga.user_group;
      if (ug?.id) {
        byId[ug.id] = ug;
      }
    });
    return Object.values(byId).sort(
      (a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
    );
  }, [assessments, collator]);

  const allUserGroupIds = useMemo(
    () => userGroups.map((ug) => ug.id),
    [userGroups]
  );

  const allCapabilityIds = useMemo(
    () =>
      flattenCapabilityTree(capabilitySet?.capabilities || []).map(
        (item) => item.id
      ),
    [capabilitySet?.capabilities]
  );

  const [selectedUserGroupIds, setSelectedUserGroupIds] = useState(null);
  const [selectedCapabilityIds, setSelectedCapabilityIds] = useState(null);
  const [selectedElementKey, setSelectedElementKey] = useState(null);
  const [showUnscoredCapabilities, setShowUnscoredCapabilities] =
    useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const prevDefaultCapabilityIdsRef = useRef(null);

  useEffect(() => {
    if (selectedUserGroupIds == null && allUserGroupIds.length > 0) {
      setSelectedUserGroupIds(allUserGroupIds);
    }
  }, [allUserGroupIds, selectedUserGroupIds]);

  const effectiveSelectedUserGroupIds = selectedUserGroupIds ?? allUserGroupIds;

  const filteredAssessments = useMemo(() => {
    const selected = new Set(effectiveSelectedUserGroupIds);
    return (assessments || []).filter((uga) => selected.has(uga.user_group_id));
  }, [assessments, effectiveSelectedUserGroupIds]);

  const { capabilityElements } = useCapabilityMetrics(assessments);
  const { metrics } = useCapabilityMetrics(filteredAssessments);

  const elementOptions = useMemo(
    () => buildCapabilityElementChartOptions(capabilityElements),
    [capabilityElements]
  );

  useEffect(() => {
    if (elementOptions.length === 0) {
      setSelectedElementKey(null);
      return;
    }

    const keys = new Set(elementOptions.map((el) => el.key));
    if (selectedElementKey != null && keys.has(selectedElementKey)) {
      return;
    }

    setSelectedElementKey(pickDefaultElementKey(elementOptions));
  }, [elementOptions, selectedElementKey]);

  const scoresByCapability = useMemo(
    () =>
      buildCapabilityElementScores({
        userGroupAssessments: filteredAssessments,
        metricsByUgaId: metrics,
      }),
    [filteredAssessments, metrics]
  );

  const scoredCapabilityIds = useMemo(() => {
    const scored = new Set(capabilitiesWithAnyScores(scoresByCapability));
    return allCapabilityIds.filter((id) => scored.has(id));
  }, [scoresByCapability, allCapabilityIds]);

  const defaultCapabilityIds = scoredCapabilityIds;

  useEffect(() => {
    if (allCapabilityIds.length === 0) {
      setSelectedCapabilityIds([]);
      prevDefaultCapabilityIdsRef.current = [];
      return;
    }

    const prevDefault = prevDefaultCapabilityIdsRef.current;
    prevDefaultCapabilityIdsRef.current = defaultCapabilityIds;

    setSelectedCapabilityIds((prev) => {
      if (prev == null) {
        return defaultCapabilityIds;
      }
      if (prevDefault != null && sameIdSet(prev, prevDefault)) {
        return defaultCapabilityIds;
      }
      const valid = new Set(allCapabilityIds);
      return prev.filter((id) => valid.has(id));
    });
  }, [allCapabilityIds, defaultCapabilityIds]);

  const effectiveSelectedCapabilityIds =
    selectedCapabilityIds ?? defaultCapabilityIds;

  const prunedCapabilities = useMemo(
    () =>
      pruneCapabilityTree(
        capabilitySet?.capabilities || [],
        effectiveSelectedCapabilityIds
      ),
    [capabilitySet?.capabilities, effectiveSelectedCapabilityIds]
  );

  // Same capability set the treemap shows (selection + ancestor parents).
  const treemapCapabilityIds = useMemo(
    () => flattenCapabilityTree(prunedCapabilities).map((item) => item.id),
    [prunedCapabilities]
  );

  const chartCapabilityIds = useMemo(() => {
    if (showUnscoredCapabilities) {
      return treemapCapabilityIds;
    }
    const scored = new Set(scoredCapabilityIds);
    return treemapCapabilityIds.filter((id) => scored.has(id));
  }, [showUnscoredCapabilities, treemapCapabilityIds, scoredCapabilityIds]);

  const visibleCapabilitiesForCharts = useMemo(
    () =>
      selectedCapabilitiesInTreeOrder(
        capabilitySet?.capabilities || [],
        chartCapabilityIds
      ),
    [capabilitySet?.capabilities, chartCapabilityIds]
  );

  const chartEmptyReason = useMemo(() => {
    if (visibleCapabilitiesForCharts.length > 0) {
      return null;
    }
    if (allCapabilityIds.length === 0) {
      return 'none-exist';
    }
    if (effectiveSelectedCapabilityIds.length === 0) {
      return 'none-selected';
    }
    if (
      !showUnscoredCapabilities &&
      treemapCapabilityIds.some((id) => !scoredCapabilityIds.includes(id))
    ) {
      return 'scored-only-empty';
    }
    return 'none-selected';
  }, [
    visibleCapabilitiesForCharts,
    allCapabilityIds,
    effectiveSelectedCapabilityIds,
    showUnscoredCapabilities,
    treemapCapabilityIds,
    scoredCapabilityIds,
  ]);

  const selectedElement = useMemo(
    () => elementOptions.find((el) => el.key === selectedElementKey),
    [elementOptions, selectedElementKey]
  );

  const userGroupFilterLabel = useMemo(
    () => getUserGroupFilterLabel(userGroups, effectiveSelectedUserGroupIds),
    [userGroups, effectiveSelectedUserGroupIds]
  );

  const capabilityFilterLabel = useMemo(
    () =>
      getCapabilityFilterLabel(
        capabilitySet?.capabilities || [],
        effectiveSelectedCapabilityIds,
        defaultCapabilityIds
      ),
    [
      capabilitySet?.capabilities,
      effectiveSelectedCapabilityIds,
      defaultCapabilityIds,
    ]
  );

  const filtersSummary = [userGroupFilterLabel, capabilityFilterLabel]
    .filter(Boolean)
    .join('; ');

  if (isLoading && !hasLoaded) {
    return <Loading />;
  }

  if (hasLoaded && assessments?.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="media text-muted mt-4 mb-4">
            <Icon icon={faSpiderWeb} size="4x" className="mr-3" fixedWidth />
            <div className="media-body">
              <p>No assessments have used this capability set yet.</p>
              <p>
                Once a user group assessment goes live with this set, scores
                will appear here.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className={styles['overview']}>
      <Card className={styles['filters-card']}>
        <CardBody className={styles['filters-card-body']}>
          <Button
            type="button"
            outline
            color="info"
            className={styles['filters-toggle']}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <Icon
              icon={faFilter}
              fixedWidth
              className={styles['filters-toggle-icon']}
            />
            <span className={styles['filters-toggle-label']}>
              {filtersOpen ? 'Filters' : filtersSummary || 'Filters'}
            </span>
            <Icon
              icon={['far', filtersOpen ? 'chevron-up' : 'chevron-down']}
              fixedWidth
              className={styles['filters-toggle-caret']}
            />
          </Button>
          <Collapse isOpen={filtersOpen}>
            <div className={styles.selectors}>
              <FormGroup>
                <Label for="capability-set-results-user-groups">
                  User groups
                </Label>
                <UserGroupFilterCombobox
                  id="capability-set-results-user-groups"
                  userGroups={userGroups}
                  selectedIds={effectiveSelectedUserGroupIds}
                  onChange={setSelectedUserGroupIds}
                  disabled={!hasLoaded}
                />
              </FormGroup>
              <FormGroup>
                <Label for="capability-set-results-capabilities">
                  Capabilities
                </Label>
                <CapabilityFilterCombobox
                  id="capability-set-results-capabilities"
                  capabilities={capabilitySet?.capabilities || []}
                  selectedIds={effectiveSelectedCapabilityIds}
                  defaultIds={defaultCapabilityIds}
                  onChange={setSelectedCapabilityIds}
                  disabled={!hasLoaded}
                />
              </FormGroup>
            </div>
          </Collapse>
        </CardBody>
      </Card>

      <Card className={styles['treemap-card']}>
        <CardBody>
          <div className={styles['element-buttons']}>
            {elementOptions.map((el) => {
              const isSelected = el.key === selectedElementKey;
              return (
                <button
                  key={el.key}
                  type="button"
                  className={classNames(
                    'btn',
                    'btn-link',
                    styles['element-button'],
                    isSelected && styles['element-button-active']
                  )}
                  onClick={() => setSelectedElementKey(el.key)}
                >
                  {isSelected && (
                    <span
                      className={classNames(
                        `palette-chart-${el.paletteIndex}`,
                        'palette-muted',
                        styles['element-button-bg']
                      )}
                      aria-hidden="true"
                    >
                      <span className="palette-bg" />
                    </span>
                  )}
                  <span className={`palette-chart-${el.paletteIndex}`}>
                    <Icon
                      icon={faSquare}
                      className={classNames(
                        'palette-text',
                        styles['element-swatch']
                      )}
                    />
                  </span>
                  <span className={styles['element-button-label']}>
                    {el.name || el.key}
                  </span>
                </button>
              );
            })}
          </div>
          <CapabilityTreemap
            capabilities={prunedCapabilities}
            scoresByCapability={scoresByCapability}
            selectedElementKey={selectedElementKey}
            selectedElementName={selectedElement?.name || selectedElementKey}
            selectedElementPaletteIndex={selectedElement?.paletteIndex}
            hasCapabilitiesInSet={allCapabilityIds.length > 0}
          />
        </CardBody>
      </Card>

      <Card className={styles['charts-card']}>
        <CardBody>
          <CustomInput
            type="checkbox"
            id="capability-set-show-unscored"
            className={styles['show-unscored']}
            label="Show capabilities without scores"
            checked={showUnscoredCapabilities}
            onChange={(event) =>
              setShowUnscoredCapabilities(event.target.checked)
            }
          />
          <CapabilityOverviewChartGrid
            elements={elementOptions}
            capabilities={visibleCapabilitiesForCharts}
            scoresByCapability={scoresByCapability}
            capabilitySetName={capabilitySet?.name}
            emptyReason={chartEmptyReason}
            thresholds={capabilitySet?.thresholds || {}}
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default CapabilitySetResults;
